import { useCallback, useEffect, useRef, useState } from "react";
import { NotificationType, type SpinningWheelResultNotification, type SpinningWheelUIReward } from "@cede/types";
import { WHEEL_SPIN_PHASES, type WheelSpinPhase, SPIN_TIMEOUT_MS } from "@/constants/spinningWheel";
import { getBiggestWin, sortRewardsBySize } from "@/utils/wheel";
import Decimal from "decimal.js";

interface UseWheelResultCollectionProps {
  rewards: SpinningWheelUIReward[];
  hubUserId?: string;
  spinPhase: WheelSpinPhase;
  onResultsCollected: (
    biggestWin: SpinningWheelUIReward,
    biggestWinIndex: number,
    allResults: SpinningWheelUIReward[],
  ) => void;
  onTimeout: () => void;
  onMultiSpinComplete: (results: SpinningWheelUIReward[]) => void;
}

/**
 * Hook for collecting and managing spinning wheel results from SSE notifications
 */
export const useWheelResultCollection = ({
  rewards,
  hubUserId,
  spinPhase,
  onResultsCollected,
  onTimeout,
  onMultiSpinComplete,
}: UseWheelResultCollectionProps) => {
  const [collectedResults, setCollectedResults] = useState<SpinningWheelUIReward[]>([]);
  const [expectedSpinCount, setExpectedSpinCount] = useState<number>(1);

  const processedNotificationIds = useRef<Set<string>>(new Set());
  const spinTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Start collecting results for a spin
   */
  const startCollecting = useCallback(
    (spinCount: number) => {
      setExpectedSpinCount(spinCount);
      setCollectedResults([]);
      processedNotificationIds.current.clear();

      // Set timeout for spin
      spinTimeoutRef.current = setTimeout(() => {
        onTimeout();
      }, SPIN_TIMEOUT_MS);
    },
    [onTimeout],
  );

  /**
   * Stop collecting and clean up
   */
  const stopCollecting = useCallback(() => {
    if (spinTimeoutRef.current) {
      clearTimeout(spinTimeoutRef.current);
      spinTimeoutRef.current = null;
    }
    setCollectedResults([]);
    setExpectedSpinCount(1);
    processedNotificationIds.current.clear();
  }, []);

  /**
   * Handle incoming notification
   */
  const handleNotification = useCallback(
    (event: CustomEvent<SpinningWheelResultNotification>) => {
      const notification = event.detail;

      if (notification.type === NotificationType.SPINNING_WHEEL_RESULT && notification.data && hubUserId) {
        const notificationId = notification.id;

        // Skip if already processed
        if (processedNotificationIds.current.has(notificationId)) {
          return;
        }

        // Skip if not in correct phase
        if (spinPhase !== WHEEL_SPIN_PHASES.WAITING_FOR_RESULT && spinPhase !== WHEEL_SPIN_PHASES.INITIAL_SPIN) {
          return;
        }

        processedNotificationIds.current.add(notificationId);

        if (notification.data) {
          const { rewardAmount, rewardSymbol } = notification.data;
          const matchingRewards = rewards.filter(
            (r) => r.rewardSymbol === rewardSymbol && r.rewardAmount.equals(new Decimal(rewardAmount)),
          );

          if (matchingRewards[0]) {
            const result: SpinningWheelUIReward = {
              ...matchingRewards[0],
              completionId: notification.data.resultId,
            };

            setCollectedResults((prev) => {
              const updated = [...prev, result];

              // Check if all results collected
              if (updated.length >= expectedSpinCount) {
                // Clear timeout
                if (spinTimeoutRef.current) {
                  clearTimeout(spinTimeoutRef.current);
                  spinTimeoutRef.current = null;
                }

                const biggestWin = getBiggestWin(updated);
                const biggestWinIndex = rewards.findIndex((r) => r.id === biggestWin.id);

                // Handle multi-spin
                if (expectedSpinCount > 1) {
                  const sortedResults = sortRewardsBySize(updated);
                  onMultiSpinComplete(sortedResults);
                }

                // Notify about results - pass the updated array directly
                onResultsCollected(biggestWin, biggestWinIndex, updated);
              }

              return updated;
            });
          }
        }
      }
    },
    [hubUserId, rewards, spinPhase, expectedSpinCount, onResultsCollected, onMultiSpinComplete],
  );

  // Listen for notifications
  useEffect(() => {
    window.addEventListener("spinning-wheel-notification", handleNotification as EventListener);
    return () => {
      window.removeEventListener("spinning-wheel-notification", handleNotification as EventListener);
    };
  }, [handleNotification]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
      }
    };
  }, []);

  return {
    collectedResults,
    expectedSpinCount,
    startCollecting,
    stopCollecting,
    isCollecting: collectedResults.length < expectedSpinCount,
  };
};
